import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import MsModel from '../components/MsModel';
import './LaptopViewer.css';
import { FaWhatsapp } from "react-icons/fa";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form } from 'react-bootstrap';
import logo from '../assets/TT logo.png';
import "driver.js/dist/driver.css";
import { driver } from "driver.js";
import { Link } from 'react-router-dom';

// Map raw model part names → user-friendly aliases
const partNameAliases = {
  "Surfacepro_Display_-1": "SurfacePro Display",
  "Key_pad_&_Mouse_pad_-2": "Keyboard & Trackpad",
  "Studio_Display_-3": "Studio Display",
  "CPU_unit_-_4": "CPU Unit",
  "Battery_-5": "Battery",
  "Charging_-6": "Charging",
  "Overheating_Fan_-7": "Overheating & Fan",
  "Motherboard_Hardware_-8": "Motherboard & Hardware",
  "Audio_Webcam_Camera_-9": "Audio / Webcam / Camera",
  "BIOS_Firmware_Software_-10": "BIOS / Firmware / Software",
  "Storage_Boot_-11": "Storage & Boot",
  "GPU_Graphics_-12": "GPU / Graphics"
};

// Issues mapped to ALIAS (not raw names)
const partIssues = {
  "SurfacePro Display": [
    "Screen flickers",
    "LCD broken",
    "No display on boot",
    "Black screen randomly",
    "Dim screen at full brightness",
    "Intermittent Display",
    "Lines across the screen",
    "Screen turns off during use",
    "Touchscreen not working",
    "External monitor not detected",
    "Cracked or broken screen",
    "Colour distortion or flicker",
    "Screen ghosting on movement"
  ],
  "Battery": [
    "Battery drains fast",
    "Battery not charging fully",
    "Laptop shuts down on battery",
    "Battery stuck at 0% or 100%",
    "Battery not detected",
    "Swollen battery",
    "Wrong battery level shown",
    "Battery heats up during use"
  ],
  "Charging": [
    "Laptop not charging",
    "Charger not recognized",
    "Loose charging port & Broken",
    "Charging only at a certain angle",
    "Power adapter overheating",
    "USB-C charging not working",
    "Slow charging",
    "No charging light indicator",
    "Charging stops randomly"
  ],
  "Overheating & Fan": [
    "Laptop gets too hot",
    "Fans running loud constantly",
    "Fan not spinning",
    "Laptop shuts off when hot",
    "Fan Error",
    "Heat from keyboard area"
  ],
  "Keyboard & Trackpad": [
    "Some keys not working",
    "Entire keyboard dead",
    "Backlight not turning on",
    "Trackpad unresponsive",
    "Cursor jumping or lagging",
    "Keyboard damage",
    "Double typing or stuck keys"
  ],
  "Motherboard & Hardware": [
    "Not powering on",
    "No POST or beeps",
    "Liquid spill on board",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Faulty IC",
    "USB ports not working",
    "Thunderbolt port failure",
    "No external monitor detection"
  ],
  "Audio / Webcam / Camera": [
    "No sound from speakers",
    "Distorted or crackling audio",
    "Mic not working",
    "Headphones not detected",
    "Webcam not turning on",
    "Black screen on webcam",
    "Camera flickers or lags",
    "Audio jack loose or broken"
  ],
  "BIOS / Firmware / Software": [
    "BIOS corrupted",
    "Locked BIOS or EFI password",
    "Failed BIOS update",
    "Mac firmware lock",
    "Stuck in update loop",
    "Missing OS after update",
    "Driver conflicts after update",
    "Frequent software crashes"
  ],
  "Storage & Boot": [
    "SSD/HDD not detected",
    "OS not booting",
    "Stuck on logo screen",
    "Boot device not found",
    "Frequent BSODs",
    "Slow boot time",
    "SSD overheating",
    "Files corrupted randomly"
  ],
  "GPU / Graphics": [
    "GPU not detected",
    "Games crash on launch",
    "Artefacts on screen",
    "Stuttering or lag in games",
    "Black screen during gameplay",
    "External GPU not working",
    "Display runs on Intel GPU only",
    "Driver crash or error"
  ],
  "Studio Display": [
    "Not powering on",
    "No POST or beeps",
    "Liquid spill on board",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Faulty IC",
    "USB ports not working",
    "Thunderbolt port failure",
    "No external monitor detection"
  ],
  "CPU Unit": [
    "Not powering on",
    "No POST or beeps",
    "Liquid spill on board",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Faulty IC",
    "USB ports not working",
    "Thunderbolt port failure",
    "No external monitor detection"
  ]
};


const MsStudio = () => {
  const [selectedPart, setSelectedPart] = useState(null); // RAW name
  const [hoveredPart, setHoveredPart] = useState(null);   // RAW name
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Convert raw name → alias for UI
  const getAlias = (rawName) => partNameAliases[rawName] || rawName;

  const hoveredAlias = getAlias(hoveredPart);
  const selectedAlias = getAlias(selectedPart);

  const initialCameraPosition = [0, 1, 2];

  // Store RAW name for highlight logic
  const handlePartClick = (rawName) => {
    setSelectedPart(rawName);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedAlias || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the product with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the product model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);


  return (
    <>
    <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
            <Link to={'/'}><img src={logo} style={{height: '160px', margin: '-40px -30px', padding: '0px'}} /></Link>
            <Button variant="info" onClick={startTour}>
              📖 Show Instructions
            </Button>
          </header>
      <div className="laptop-viewer-container">
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <MsModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart} // RAW name
            />
            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>All Parts</h3>
              <ul>
                {Object.entries(partNameAliases).map(([rawName, alias], i) => (
                  <li
                    key={i}
                    onClick={() => handlePartClick(rawName)} // RAW name
                    style={{ cursor: 'pointer' }}
                  >
                    {alias}
                  </li>
                ))}
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedAlias} Issues</h3>
              <ul>
                {(partIssues[selectedAlias] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: 'pointer' }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MsStudio;
